import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Upload, AlertTriangle, Image, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const AlbumArtManager = ({ tracks, onUpdateTrack }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer?.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  }, []);

  const handleFileInput = (e) => {
    const files = e.target?.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleApplyToTrack = async () => {
    if (!selectedTrack || !previewUrl) {
      toast.error("Please select a track and upload an image");
      return;
    }

    const result = await onUpdateTrack(selectedTrack.id, {
      album_art_url: previewUrl,
    });

    if (result.success) {
      toast.success(`Album art applied to "${selectedTrack.title}"`);
      setPreviewUrl(null);
      setSelectedTrack(null);
    } else {
      toast.error("Failed to apply album art");
    }
  };

  const clearPreview = () => {
    setPreviewUrl(null);
  };

  // Get tracks with existing album art
  const tracksWithArt = tracks.filter((t) => t.album_art_url);
  const tracksWithoutArt = tracks.filter((t) => !t.album_art_url);

  return (
    <div
      className="bg-meta-surface border border-meta-border h-full flex flex-col"
      data-testid="album-art-manager"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-meta-border">
        <div className="flex items-center gap-3">
          <Image className="text-meta-neon" size={20} />
          <div>
            <h2 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
              Album Art Manager
            </h2>
            <p className="font-sans font-semibold text-meta-text">Cover Art Studio</p>
          </div>
        </div>
      </div>

      {/* PERMANENT WARNING BANNER */}
      <div className="warning-banner mx-6 mt-4" data-testid="no-portraits-warning">
        <AlertTriangle size={18} />
        <div>
          <p className="font-mono text-sm font-medium">Design Rule: No Portraits</p>
          <p className="text-xs opacity-80">
            All album covers must follow the strict "No portraits of me" design guideline.
          </p>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Upload Zone */}
          <div className="space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
              Upload New Art
            </h3>

            {/* Drop Zone */}
            <div
              className={`relative border-2 border-dashed rounded-sm transition-all duration-200 ${
                dragActive
                  ? "border-meta-neon bg-meta-neon-dim"
                  : "border-meta-border hover:border-meta-neon/50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="dropzone"
            >
              {previewUrl ? (
                <div className="relative aspect-square">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={clearPreview}
                    className="absolute top-2 right-2 p-1 bg-meta-danger text-white rounded-sm hover:bg-red-600 transition-colors"
                    data-testid="clear-preview-btn"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center aspect-square cursor-pointer">
                  <Upload
                    size={40}
                    className={`mb-3 ${
                      dragActive ? "text-meta-neon" : "text-meta-text-muted"
                    }`}
                  />
                  <p className="text-meta-text-secondary text-sm mb-1">
                    Drag & drop cover art here
                  </p>
                  <p className="text-meta-text-muted text-xs">or click to browse</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileInput}
                    className="hidden"
                    data-testid="file-input"
                  />
                </label>
              )}
            </div>

            {/* Track Selection */}
            {previewUrl && (
              <div className="space-y-2">
                <h4 className="font-mono text-xs text-meta-text-muted uppercase">
                  Apply to Track
                </h4>
                <div className="space-y-1 max-h-32 overflow-auto">
                  {tracks.map((track) => (
                    <button
                      key={track.id}
                      onClick={() => setSelectedTrack(track)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm font-mono transition-colors ${
                        selectedTrack?.id === track.id
                          ? "bg-meta-neon-dim text-meta-neon border border-meta-neon"
                          : "bg-meta-base text-meta-text-secondary hover:bg-meta-elevated"
                      }`}
                      data-testid={`select-track-${track.id}`}
                    >
                      <span className="truncate">{track.title}</span>
                      {selectedTrack?.id === track.id && <Check size={14} />}
                    </button>
                  ))}
                </div>
                <Button
                  onClick={handleApplyToTrack}
                  disabled={!selectedTrack}
                  className="w-full bg-meta-neon text-meta-base hover:shadow-neon disabled:opacity-50"
                  data-testid="apply-art-btn"
                >
                  Apply to {selectedTrack?.title || "Track"}
                </Button>
              </div>
            )}
          </div>

          {/* Existing Album Art */}
          <div className="space-y-4">
            <h3 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted">
              Current Album Art ({tracksWithArt.length})
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {tracksWithArt.map((track) => (
                <motion.div
                  key={track.id}
                  whileHover={{ scale: 1.02 }}
                  className="relative group"
                  data-testid={`album-art-${track.id}`}
                >
                  <img
                    src={track.album_art_url}
                    alt={track.title}
                    className="w-full aspect-square object-cover border border-meta-border"
                  />
                  <div className="absolute inset-0 bg-meta-base/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                    <p className="p-2 font-mono text-xs text-meta-text truncate w-full">
                      {track.title}
                    </p>
                  </div>
                </motion.div>
              ))}
              {tracksWithArt.length === 0 && (
                <p className="col-span-2 text-meta-text-muted text-sm py-8 text-center">
                  No album art uploaded yet
                </p>
              )}
            </div>

            {/* Tracks without art */}
            {tracksWithoutArt.length > 0 && (
              <>
                <h3 className="font-mono text-xs uppercase tracking-wider text-meta-text-muted mt-6">
                  Missing Art ({tracksWithoutArt.length})
                </h3>
                <div className="space-y-1">
                  {tracksWithoutArt.map((track) => (
                    <div
                      key={track.id}
                      className="flex items-center gap-2 px-3 py-2 bg-meta-base text-meta-text-muted text-sm font-mono"
                    >
                      <div className="w-8 h-8 bg-meta-elevated flex items-center justify-center">
                        <Image size={14} />
                      </div>
                      <span className="truncate">{track.title}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
